// Where each language lives (owner's decision, 2026-10-18): English is the site's own address, https://realestatechannel.ca/..., French
// sits under https://realestatechannel.ca/fr/... There is no /en in an address a visitor, a link or a crawler sees.
//
// Inside, the pages still live under app/[lang]/ (the segment is "en" or "fr"), and proxy.ts connects the two: a request without a language
// is served as /en/..., a request that still carries /en is sent to the same address without it (a permanent redirect), /fr is itself.
// Everything that WRITES an address - a link, a canonical, a sitemap line, an email, a notification - goes through the functions here, so
// the rule is in one place.

export type Lang = 'en' | 'fr';

/** What an address of this language starts with: nothing for English, "/fr" for French. Followed by "/something". */
export const langPrefix = (lang: string): '' | '/fr' => (lang === 'fr' ? '/fr' : '');

/** The home page of this language: "/" or "/fr". Safe to follow with "?" or "#". */
export const langHome = (lang: string): '/' | '/fr' => (lang === 'fr' ? '/fr' : '/');

/** An address in this language. `path` begins with "/", "?" or "#", or is empty for the home page. */
export function localePath(lang: string, path = ''): string {
  if (!path || path === '/') return langHome(lang);
  if (path[0] === '/') return `${langPrefix(lang)}${path}`;
  return `${langHome(lang)}${path}`;
}

/** The language of an address as the visitor sees it, and the rest of it. */
export function splitLocale(pathname: string): { lang: Lang; rest: string } {
  const m = /^\/(en|fr)(?=\/|$|\?|#)/.exec(pathname);
  const rest = m ? pathname.slice(m[0].length) : pathname;
  return { lang: m?.[1] === 'fr' ? 'fr' : 'en', rest: rest.startsWith('/') ? rest : rest ? `/${rest}` : '/' };
}

/** The address a visitor sees, turned into the one the pages are filed under: "/blog" is "/en/blog", "/fr/blog" stays. */
export function toInternal(pathname: string): string {
  if (/^\/(en|fr)(\/|$)/.test(pathname)) return pathname;
  return `/en${pathname === '/' ? '' : pathname}`;
}

/** The other way: "/en/blog" is "/blog", "/en" is "/", "/fr/blog" stays. */
export function toExternal(pathname: string): string {
  if (pathname === '/en') return '/';
  return pathname.startsWith('/en/') ? pathname.slice(3) : pathname;
}
